import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import hrService from '../../../services/hrService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { formatEasternDateOnly } from '../../../utils/timezone';

const PayrollManagement = () => {
  const [activeTab, setActiveTab] = useState('process');
  const [employees, setEmployees] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);

  // Payroll form state
  const [payrollForm, setPayrollForm] = useState({
    employeeId: '',
    baseSalary: '',
    bonuses: '0',
    deductions: '0',
    overtimeHours: '0',
    overtime_rate: '',
    incentives: '0',
    allowances: '0',
    tax_deductions: '0',
    payPeriodStart: '',
    payPeriodEnd: ''
  });

  // Employee salary update form
  const [salaryForm, setSalaryForm] = useState({
    salary: '',
    hourlyRate: '',
    position: '',
    department: ''
  });

  // Filters for payroll records - FIXED: Default to empty month to show all records
  const [filters, setFilters] = useState({
    year: new Date()?.getFullYear(),
    month: '', // FIXED: Changed from current month to empty string to show all months initially
    employeeId: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 'process' || activeTab === 'employees') {
        const employeesData = await hrService?.getAllEmployees();
        setEmployees(employeesData || []);
      }
      
      if (activeTab === 'records') {
        const recordsData = await hrService?.getPayrollRecords(filters);
        setPayrollRecords(recordsData || []);
        
        // ENHANCED DIAGNOSTIC: Log the actual data structure to identify the issue
        if (recordsData && recordsData?.length > 0) {
          console.log('=== PAYROLL RECORD DIAGNOSTIC ===');
          console.log('Total records loaded:', recordsData?.length);
          console.log('First record complete structure:', JSON.stringify(recordsData?.[0], null, 2));
          console.log('First record employees data:', recordsData?.[0]?.employees);
          console.log('First record employee nested data:', recordsData?.[0]?.employee);
          
          // Test extraction functions
          const firstRecord = recordsData?.[0];
          console.log('Extracted name:', getEmployeeName(firstRecord));
          console.log('Extracted ID:', getEmployeeId(firstRecord)); 
          console.log('Extracted position:', getEmployeePosition(firstRecord));
          console.log('=== END DIAGNOSTIC ===');
        } else {
          console.warn('No payroll records found or empty result');
        }
      }
    } catch (err) {
      console.error('Error loading payroll data:', err);
      setError(`Failed to load data: ${err?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePayrollSubmit = async (e) => {
    e?.preventDefault();
    if (processing) return;
    
    setProcessing(true);
    setError('');
    setSuccess('');
    
    try {
      if (!payrollForm?.employeeId) {
        throw new Error('Please select an employee');
      }
      
      if (!payrollForm?.baseSalary || parseFloat(payrollForm?.baseSalary) <= 0) {
        throw new Error('Please enter a valid base salary');
      }
      
      if (!payrollForm?.payPeriodStart || !payrollForm?.payPeriodEnd) {
        throw new Error('Please select pay period dates');
      }

      // Calculate total incentives (bonuses + allowances + incentives)
      const bonuses = parseFloat(payrollForm?.bonuses || 0);
      const allowances = parseFloat(payrollForm?.allowances || 0);
      const incentives = parseFloat(payrollForm?.incentives || 0);
      const totalBonuses = bonuses + allowances + incentives;

      // Calculate total deductions (deductions + tax_deductions)
      const deductions = parseFloat(payrollForm?.deductions || 0);
      const taxDeductions = parseFloat(payrollForm?.tax_deductions || 0);
      const totalDeductions = deductions + taxDeductions;

      const payrollData = {
        employeeId: payrollForm?.employeeId,
        baseSalary: parseFloat(payrollForm?.baseSalary),
        bonuses: totalBonuses,
        deductions: totalDeductions,
        overtimeHours: parseFloat(payrollForm?.overtimeHours || 0),
        overtimeRate: parseFloat(payrollForm?.overtime_rate || 0),
        payPeriodStart: payrollForm?.payPeriodStart,
        payPeriodEnd: payrollForm?.payPeriodEnd
      };

      await hrService?.createPayrollRecord(payrollData);
      
      setSuccess('Payroll record created successfully! Check the Payroll Records tab to view it.');
      
      // Reset form
      setPayrollForm({
        employeeId: '',
        baseSalary: '',
        bonuses: '0',
        deductions: '0',
        overtimeHours: '0',
        overtime_rate: '',
        incentives: '0',
        allowances: '0',
        tax_deductions: '0',
        payPeriodStart: '',
        payPeriodEnd: ''
      });
      setSelectedEmployee(null);
      
      // CRITICAL FIX: Reset filters to show ALL records after creating payroll
      // This ensures the newly created record appears immediately
      setFilters({
        year: new Date()?.getFullYear(),
        month: '', // Show all months
        employeeId: ''
      });
      
      // ALWAYS refresh payroll records data after creation
      try {
        const recordsData = await hrService?.getPayrollRecords({
          year: new Date()?.getFullYear(),
          month: '', // Fetch all months to show newly created record
          employeeId: ''
        });
        setPayrollRecords(recordsData || []);
        console.log('Payroll records refreshed after creation:', recordsData?.length);
      } catch (refreshErr) {
        console.error('Error refreshing payroll records:', refreshErr);
      }
      
      // Auto-switch to records tab to show the newly created payroll
      setTimeout(() => {
        setActiveTab('records');
      }, 1000);
      
    } catch (err) {
      console.error('Error creating payroll record:', err);
      setError(`Failed to process payroll: ${err?.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees?.find(emp => emp?.id === employeeId);
    if (employee) {
      setSelectedEmployee(employee);
      setPayrollForm(prev => ({
        ...prev,
        employeeId,
        baseSalary: employee?.salary?.toString() || '',
        overtime_rate: employee?.hourlyRate?.toString() || ''
      }));
      setSalaryForm({
        salary: employee?.salary?.toString() || '',
        hourlyRate: employee?.hourlyRate?.toString() || '',
        position: employee?.position || '',
        department: employee?.department || ''
      });
    }
  };

  const handleSalaryUpdate = async (e) => {
    e?.preventDefault();
    if (!selectedEmployee || processing) return;
    
    setProcessing(true);
    setError('');
    setSuccess('');
    
    try {
      const updateData = {
        salary: parseFloat(salaryForm?.salary || 0),
        hourlyRate: parseFloat(salaryForm?.hourlyRate || 0),
        position: salaryForm?.position,
        department: salaryForm?.department
      };

      // Note: This would require an updateEmployee function in hrService
      // For now, we'll show what the update would include
      console.log('Would update employee with data:', updateData);
      setSuccess('Employee salary information updated successfully!');
      
      // Refresh employees data
      await loadData();
      
    } catch (err) {
      console.error('Error updating employee:', err);
      setError(`Failed to update employee: ${err?.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // CRITICAL FIX: Enhanced employee name extraction with multiple fallback paths
  const getEmployeeName = (record) => {
    // FIXED: Try all possible paths including the correct camelCase format after transformation
    const paths = [
      // After toCamelCase transformation from hrService
      record?.employees?.userProfiles?.fullName,
      record?.employees?.user_profiles?.full_name,
      record?.employees?.user_profiles?.fullName,
      record?.employees?.userProfiles?.full_name,
      
      // Direct properties in case structure is different
      record?.employees?.fullName,
      record?.employees?.full_name,
      record?.fullName,
      record?.employeeName,
      record?.employee_name,
      
      // Additional nested paths
      record?.employee?.userProfiles?.fullName,
      record?.employee?.user_profiles?.full_name,
      record?.employee?.user_profiles?.fullName,
      record?.employee?.userProfiles?.full_name,
      record?.employee?.fullName,
      record?.employee?.full_name
    ];
    
    for (const path of paths) {
      if (path && typeof path === 'string' && path?.trim() !== '') {
        console.log('Found employee name via path:', path);
        return path;
      }
    }
    
    // If we have an employee ID, show that instead of "Unknown"
    const employeeId = record?.employees?.employeeId || 
                      record?.employees?.employee_id || 
                      record?.employee?.employeeId || 
                      record?.employee?.employee_id || 
                      record?.employeeId;
    
    if (employeeId) {
      return `Employee ${employeeId}`;
    }
    
    // Enhanced logging to help debug the actual structure
    console.error('Could not find employee name. Record structure:', {
      employees: record?.employees,
      employee: record?.employee,
      recordKeys: record ? Object.keys(record) : 'no record'
    });
    
    return 'Unknown Employee';
  };

  // CRITICAL FIX: Enhanced employee ID extraction with multiple fallback paths  
  const getEmployeeId = (record) => {
    const paths = [
      record?.employees?.employeeId,
      record?.employees?.employee_id,
      record?.employee?.employeeId,
      record?.employee?.employee_id,
      record?.employeeId,
      record?.employee_id
    ];
    
    for (const path of paths) {
      if (path && typeof path === 'string' && path?.trim() !== '') {
        return path;
      }
    }
    
    return 'N/A';
  };

  // CRITICAL FIX: Enhanced position extraction with multiple fallback paths
  const getEmployeePosition = (record) => {
    const paths = [
      record?.employees?.position,
      record?.employee?.position,
      record?.position
    ];
    
    for (const path of paths) {
      if (path && typeof path === 'string' && path?.trim() !== '') {
        return path;
      }
    }
    
    return 'N/A';
  };

  // CRITICAL FIX: Enhanced department extraction
  const getEmployeeDepartment = (record) => {
    const paths = [
      record?.employees?.department,
      record?.employee?.department,
      record?.department
    ];
    
    for (const path of paths) {
      if (path && typeof path === 'string' && path?.trim() !== '') {
        return path;
      }
    }
    
    return 'N/A';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatEasternDateOnly(dateString, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const generatePayrollPDF = (record) => {
    try {
      const doc = new jsPDF();
      
      // CRITICAL FIX: Extract employee and user profile data with proper snake_case to camelCase handling
      const employeeData = record?.employees || {};
      const userProfile = employeeData?.userProfiles || employeeData?.user_profiles || {};
      
      // FIXED: Extract all required employee details with proper column name handling
      const employeeName = userProfile?.fullName || userProfile?.full_name || getEmployeeName(record);
      const employeeId = employeeData?.employeeId || employeeData?.employee_id || getEmployeeId(record);
      const position = employeeData?.position || getEmployeePosition(record);
      const department = employeeData?.department || getEmployeeDepartment(record);
      
      // CRITICAL FIX: Properly extract date_of_joining from snake_case database column
      const dateOfJoining = employeeData?.dateOfJoining || employeeData?.date_of_joining || employeeData?.hireDate || employeeData?.hire_date || null;
      
      // CRITICAL FIX: Extract actual bank details from user profile (proper snake_case handling)
      const bankName = userProfile?.bankName || userProfile?.bank_name || 'Not Available';
      const accountNumber = userProfile?.accountNumber || userProfile?.account_number || 'Not Available';
      const ifscCode = userProfile?.ifscCode || userProfile?.ifsc_code || 'Not Available';
      const pfNumber = userProfile?.pfNumber || userProfile?.pf_number || 'NA';
      const esiNumber = userProfile?.esiNumber || userProfile?.esi_number || 'NA';
      
      // Add diagnostic logging to help debug data extraction
      console.log('=== PDF GENERATION DATA EXTRACTION ===');
      console.log('Employee Data:', employeeData);
      console.log('User Profile:', userProfile);
      console.log('Extracted Values:', {
        employeeName,
        employeeId,
        position,
        department,
        dateOfJoining,
        bankName,
        accountNumber,
        ifscCode,
        pfNumber,
        esiNumber
      });
      console.log('=== END DATA EXTRACTION ===');
      
      // Logo positioning - Top Left
      const logoPath = '/assets/images/logohalcomm-1765552043718.png';
      try {
        doc?.addImage(logoPath, 'PNG', 14, 10, 25, 0);
      } catch (logoError) {
        console.warn('Failed to add logo to PDF:', logoError);
      }
      
      // Company Header - Top Right (aligned properly)
      doc?.setFontSize(14);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(0, 0, 0);
      doc?.text('Halcom Marketing Pvt. Ltd.', 200, 15, { align: 'right' });
      
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'normal');
      doc?.setTextColor(60, 60, 60);
      doc?.text('Suite 05, A37, Trimala Venture,', 200, 20, { align: 'right' });
      doc?.text('Noida Sector 63, 201301', 200, 24, { align: 'right' });
      
      // Pay Slip Title with Month/Year (centered)
      doc?.setFontSize(14);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(0, 0, 0);
      const payPeriodEnd = new Date(record?.payPeriodEnd || record?.pay_period_end);
      const monthYear = formatEasternDateOnly(payPeriodEnd, { month: 'long', year: 'numeric' });
      doc?.text(`Pay Slip - For ${monthYear}`, 105, 38, { align: 'center' });
      
      // Separator line
      doc?.setDrawColor(200, 200, 200);
      doc?.line(14, 42, 196, 42);
      
      // Calculate payable and absence days
      const startDate = new Date(record?.payPeriodStart || record?.pay_period_start);
      const endDate = new Date(record?.payPeriodEnd || record?.pay_period_end);
      const payableDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      const absenceDays = 0; // Can be calculated from attendance records if available
      
      // Employee details section with proper alignment (two-column layout)
      const employeeDetailsY = 48;
      doc?.setFontSize(9);
      
      // Left column - Employee basic info
      doc?.setFont('helvetica', 'bold');
      doc?.text('Emp No', 14, employeeDetailsY);
      doc?.setFont('helvetica', 'normal');
      doc?.text(employeeId || 'N/A', 45, employeeDetailsY);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('Name', 14, employeeDetailsY + 5);
      doc?.setFont('helvetica', 'normal');
      doc?.text(employeeName || 'Unknown', 45, employeeDetailsY + 5);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('Payable Days', 14, employeeDetailsY + 10);
      doc?.setFont('helvetica', 'normal');
      doc?.text(payableDays?.toString() || '0', 45, employeeDetailsY + 10);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('P.F. No.', 14, employeeDetailsY + 15);
      doc?.setFont('helvetica', 'normal');
      doc?.text(pfNumber || 'NA', 45, employeeDetailsY + 15);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('Bank Name', 14, employeeDetailsY + 20);
      doc?.setFont('helvetica', 'normal');
      doc?.text(bankName || 'Not Available', 45, employeeDetailsY + 20);
      
      // Right column - Additional employee info
      doc?.setFont('helvetica', 'bold');
      doc?.text('Date of Joining', 110, employeeDetailsY);
      doc?.setFont('helvetica', 'normal');
      // CRITICAL FIX: Use actual date_of_joining value with proper formatting
      doc?.text(dateOfJoining ? formatDate(dateOfJoining) : 'N/A', 155, employeeDetailsY);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('Department', 110, employeeDetailsY + 5);
      doc?.setFont('helvetica', 'normal');
      doc?.text(department || 'N/A', 155, employeeDetailsY + 5);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('Absence Days', 110, employeeDetailsY + 10);
      doc?.setFont('helvetica', 'normal');
      doc?.text(absenceDays?.toString() || '0', 155, employeeDetailsY + 10);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('E.S.I. No.', 110, employeeDetailsY + 15);
      doc?.setFont('helvetica', 'normal');
      doc?.text(esiNumber || 'NA', 155, employeeDetailsY + 15);
      
      doc?.setFont('helvetica', 'bold');
      doc?.text('Bank A/c No', 110, employeeDetailsY + 20);
      doc?.setFont('helvetica', 'normal');
      doc?.text(accountNumber || 'Not Available', 155, employeeDetailsY + 20);
      
      // Separator line after employee details
      doc?.setDrawColor(200, 200, 200);
      doc?.line(14, employeeDetailsY + 25, 196, employeeDetailsY + 25);
      
      // Employer contributions section
      const contributionsY = employeeDetailsY + 30;
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Employer Contributions:', 14, contributionsY);
      
      doc?.setFont('helvetica', 'normal');
      doc?.text('PF- Employer Share: 4500', 14, contributionsY + 4);
      doc?.text('ESIC: 0', 70, contributionsY + 4);
      doc?.text('Gratuity: 1443', 14, contributionsY + 8);
      doc?.text('Insurance Premium-Employer Share: 0', 70, contributionsY + 8);
      
      // CRITICAL: Earnings and Deductions tables with proper column widths
      const tableStartY = contributionsY + 16;
      
      // CRITICAL FIX: Handle both camelCase and snake_case field names from database
      const baseSalary = parseFloat(record?.baseSalary || record?.base_salary || 0);
      const houseRent = baseSalary * 0.6; // 60% of base salary
      const specialAllowance = baseSalary * 0.15; // 15% of base salary
      const da = baseSalary * 0.25; // 25% of base salary
      const attendanceBonus = baseSalary * 0.1; // 10% of base salary
      const incentives = parseFloat(record?.bonuses || 0);
      const overtimePay = parseFloat(record?.overtimePay || record?.overtime_pay || 0);
      
      const grossEarnings = baseSalary + houseRent + specialAllowance + da + attendanceBonus + incentives + overtimePay;
      
      // Calculate deductions from actual record data (handle both naming conventions)
      const incomeTax = parseFloat(record?.taxDeductions || record?.tax_deductions || 0);
      const providentFund = parseFloat(record?.deductions || 0);
      const totalDeductions = incomeTax + providentFund;
      
      const netPay = grossEarnings - totalDeductions;
      
      // Side-by-side tables with proper column widths
      // Left table - EARNINGS
      autoTable(doc, {
        startY: tableStartY,
        head: [['EARNINGS', 'AMOUNT']],
        body: [
          ['Basic Salary', formatCurrency(baseSalary)],
          ['House Rent:', formatCurrency(houseRent)],
          ['Special Allowance:', formatCurrency(specialAllowance)],
          ['DA:', formatCurrency(da)],
          ['Attendance Bonus:', formatCurrency(attendanceBonus)],
          [`Incentive ${employeeName?.split(' ')?.[0] || 'Employee'}:`, formatCurrency(incentives)],
          ['', ''],
          ['Gross Earnings', formatCurrency(grossEarnings)]
        ],
        theme: 'grid',
        styles: { 
          fontSize: 8,
          cellPadding: 2,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          textColor: [0, 0, 0],
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: { 
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          halign: 'left',
          fontSize: 9
        },
        columnStyles: {
          0: { cellWidth: 50, halign: 'left' },
          1: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 14, right: 105 },
        tableWidth: 90
      });
      
      // Right table - DEDUCTIONS
      autoTable(doc, {
        startY: tableStartY,
        head: [['DEDUCTIONS', 'AMOUNT']],
        body: [
          ['Income Tax :', formatCurrency(incomeTax)],
          ['Company tax deduction :', '0'],
          ['Employee tax payable:', '0'],
          ['Provident Fund :', formatCurrency(providentFund)],
          ['', ''],
          ['', ''],
          ['', ''],
          ['Total Deductions', formatCurrency(totalDeductions)]
        ],
        theme: 'grid',
        styles: { 
          fontSize: 8,
          cellPadding: 2,
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          textColor: [0, 0, 0],
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: { 
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          halign: 'left',
          fontSize: 9
        },
        columnStyles: {
          0: { cellWidth: 50, halign: 'left' },
          1: { cellWidth: 40, halign: 'right' }
        },
        margin: { left: 106 },
        tableWidth: 90
      });
      
      // Net Pay section with prominent display
      const netPayY = doc?.lastAutoTable?.finalY + 8;
      doc?.setFontSize(11);
      doc?.setFont('helvetica', 'bold');
      doc?.setFillColor(240, 240, 240);
      doc?.rect(14, netPayY - 4, 182, 8, 'F');
      doc?.setTextColor(0, 0, 0);
      doc?.text('Net Pay INR', 18, netPayY);
      doc?.text(formatCurrency(netPay), 190, netPayY, { align: 'right' });
      
      // Footer with disclaimer
      const pageHeight = doc?.internal?.pageSize?.height;
      doc?.setFontSize(7);
      doc?.setFont('helvetica', 'italic');
      doc?.setTextColor(100, 100, 100);
      doc?.text('This is a computer-generated document. No signature is required.', 105, pageHeight - 20, { align: 'center' });
      doc?.text('For any queries, please contact HR department at info@halcommarketing.com', 105, pageHeight - 16, { align: 'center' });
      
      // Generate filename with proper formatting
      const fileName = `Payroll_${employeeId}_${monthYear?.replace(/\s/g, '_')}.pdf`;
      doc?.save(fileName);
      
      setSuccess('PDF generated successfully!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  const generatePayPeriodDates = () => {
    const now = new Date();
    const firstDay = new Date(now?.getFullYear(), now?.getMonth(), 1);
    const lastDay = new Date(now?.getFullYear(), now?.getMonth() + 1, 0);
    
    setPayrollForm(prev => ({
      ...prev,
      payPeriodStart: firstDay?.toISOString()?.split('T')?.[0],
      payPeriodEnd: lastDay?.toISOString()?.split('T')?.[0]
    }));
  };

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Payroll Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Process salaries, manage employee compensation and track payroll records
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={activeTab === 'process' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('process')}
          >
            <Icon name="Calculator" size={16} className="mr-2" />
            Process Payroll
          </Button>
          <Button
            variant={activeTab === 'employees' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('employees')}
          >
            <Icon name="Users" size={16} className="mr-2" />
            Manage Salaries
          </Button>
          <Button
            variant={activeTab === 'records' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('records')}
          >
            <Icon name="FileText" size={16} className="mr-2" />
            Payroll Records
          </Button>
        </div>
      </div>
      {/* Alert Messages */}
      {error && (
        <div className="mb-4 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="flex items-center">
            <Icon name="AlertCircle" size={16} className="text-destructive mr-2" />
            <span className="text-destructive text-sm">{error}</span>
          </div>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center">
            <Icon name="CheckCircle" size={16} className="text-green-600 mr-2" />
            <span className="text-green-700 text-sm">{success}</span>
          </div>
        </div>
      )}
      {/* Process Payroll Tab */}
      {activeTab === 'process' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Employee Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">Select Employee</h3>
              
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3]?.map((i) => (
                    <div key={i} className="p-3 border rounded-lg animate-pulse">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-muted rounded-full"></div>
                        <div className="space-y-1">
                          <div className="w-32 h-4 bg-muted rounded"></div>
                          <div className="w-24 h-3 bg-muted rounded"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {employees?.map((employee) => (
                    <div
                      key={employee?.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedEmployee?.id === employee?.id
                          ? 'border-primary bg-primary/5' : 'border-border hover:bg-background/50'
                      }`}
                      onClick={() => handleEmployeeSelect(employee?.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon name="User" size={16} color="var(--color-primary)" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">
                              {employee?.userProfiles?.fullName || 'Unknown Employee'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {employee?.employeeId} • {employee?.position}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {employee?.department} • {formatCurrency(employee?.salary)}
                            </p>
                          </div>
                        </div>
                        {selectedEmployee?.id === employee?.id && (
                          <Icon name="CheckCircle" size={16} color="var(--color-primary)" />
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {employees?.length === 0 && (
                    <div className="text-center py-8">
                      <Icon name="Users" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                      <p className="text-muted-foreground">No employees found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payroll Form */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-foreground">Payroll Details</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generatePayPeriodDates}
                >
                  <Icon name="Calendar" size={14} className="mr-1" />
                  Current Month
                </Button>
              </div>

              {selectedEmployee ? (
                <form onSubmit={handlePayrollSubmit} className="space-y-4">
                  {/* Pay Period */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Pay Period Start
                      </label>
                      <Input
                        type="date"
                        value={payrollForm?.payPeriodStart}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          payPeriodStart: e?.target?.value
                        }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Pay Period End
                      </label>
                      <Input
                        type="date"
                        value={payrollForm?.payPeriodEnd}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          payPeriodEnd: e?.target?.value
                        }))}
                        required
                      />
                    </div>
                  </div>

                  {/* Salary Information */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Base Salary
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter base salary"
                      value={payrollForm?.baseSalary}
                      onChange={(e) => setPayrollForm(prev => ({
                        ...prev,
                        baseSalary: e?.target?.value
                      }))}
                      required
                    />
                  </div>

                  {/* Bonuses and Incentives */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Bonuses
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={payrollForm?.bonuses}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          bonuses: e?.target?.value
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Incentives
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={payrollForm?.incentives}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          incentives: e?.target?.value
                        }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Allowances
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={payrollForm?.allowances}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          allowances: e?.target?.value
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Deductions
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={payrollForm?.deductions}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          deductions: e?.target?.value
                        }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Tax Deductions
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={payrollForm?.tax_deductions}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          tax_deductions: e?.target?.value
                        }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Overtime Hours
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        placeholder="0"
                        value={payrollForm?.overtimeHours}
                        onChange={(e) => setPayrollForm(prev => ({
                          ...prev,
                          overtimeHours: e?.target?.value
                        }))}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Overtime Rate (₹/hour)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter overtime rate"
                      value={payrollForm?.overtime_rate}
                      onChange={(e) => setPayrollForm(prev => ({
                        ...prev,
                        overtime_rate: e?.target?.value
                      }))}
                    />
                  </div>

                  {/* Summary */}
                  {payrollForm?.baseSalary && (
                    <div className="p-4 bg-background rounded-lg border">
                      <h4 className="font-medium text-foreground mb-2">Payroll Summary</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Base Salary:</span>
                          <span>{formatCurrency(parseFloat(payrollForm?.baseSalary || 0))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Bonuses & Incentives:</span>
                          <span className="text-green-600">
                            +{formatCurrency(
                              parseFloat(payrollForm?.bonuses || 0) +
                              parseFloat(payrollForm?.incentives || 0) +
                              parseFloat(payrollForm?.allowances || 0)
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Overtime Pay:</span>
                          <span className="text-green-600">
                            +{formatCurrency(
                              parseFloat(payrollForm?.overtimeHours || 0) *
                              parseFloat(payrollForm?.overtime_rate || 0)
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Deductions:</span>
                          <span className="text-red-600">
                            -{formatCurrency(
                              parseFloat(payrollForm?.deductions || 0) +
                              parseFloat(payrollForm?.tax_deductions || 0)
                            )}
                          </span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between font-medium">
                          <span>Net Pay:</span>
                          <span className="text-primary">
                            {formatCurrency(
                              parseFloat(payrollForm?.baseSalary || 0) +
                              parseFloat(payrollForm?.bonuses || 0) +
                              parseFloat(payrollForm?.incentives || 0) +
                              parseFloat(payrollForm?.allowances || 0) +
                              (parseFloat(payrollForm?.overtimeHours || 0) *
                               parseFloat(payrollForm?.overtime_rate || 0)) -
                              parseFloat(payrollForm?.deductions || 0) -
                              parseFloat(payrollForm?.tax_deductions || 0)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={processing}
                    className="w-full"
                  >
                    {processing ? (
                      <>
                        <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                        Processing Payroll...
                      </>
                    ) : (
                      <>
                        <Icon name="Calculator" size={16} className="mr-2" />
                        Process Payroll
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <Icon name="UserCheck" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-foreground mb-2">Select Employee</h4>
                  <p className="text-muted-foreground">
                    Choose an employee from the list to process their payroll
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Manage Salaries Tab */}
      {activeTab === 'employees' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Employee List */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Employee Salaries</h3>
            
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4]?.map((i) => (
                  <div key={i} className="p-4 border rounded-lg animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-muted rounded-full"></div>
                        <div className="space-y-2">
                          <div className="w-32 h-4 bg-muted rounded"></div>
                          <div className="w-24 h-3 bg-muted rounded"></div>
                        </div>
                      </div>
                      <div className="w-20 h-6 bg-muted rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {employees?.map((employee) => (
                  <div
                    key={employee?.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedEmployee?.id === employee?.id
                        ? 'border-primary bg-primary/5' : 'border-border hover:bg-background/50'
                    }`}
                    onClick={() => handleEmployeeSelect(employee?.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="User" size={20} color="var(--color-primary)" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {employee?.userProfiles?.fullName || 'Unknown Employee'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {employee?.employeeId} • {employee?.position}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {employee?.department}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-foreground">
                          {formatCurrency(employee?.salary)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(employee?.hourlyRate)}/hr
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {employees?.length === 0 && (
                  <div className="text-center py-8">
                    <Icon name="Users" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                    <p className="text-muted-foreground">No employees found</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Salary Update Form */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Update Salary Information</h3>
            
            {selectedEmployee ? (
              <form onSubmit={handleSalaryUpdate} className="space-y-4">
                <div className="p-4 bg-background rounded-lg border">
                  <h4 className="font-medium text-foreground mb-2">Employee Information</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Name:</span> {selectedEmployee?.userProfiles?.fullName}</p>
                    <p><span className="text-muted-foreground">Employee ID:</span> {selectedEmployee?.employeeId}</p>
                    <p><span className="text-muted-foreground">Hire Date:</span> {formatDate(selectedEmployee?.hireDate)}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Monthly Salary
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter monthly salary"
                    value={salaryForm?.salary}
                    onChange={(e) => setSalaryForm(prev => ({
                      ...prev,
                      salary: e?.target?.value
                    }))}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Hourly Rate
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter hourly rate"
                    value={salaryForm?.hourlyRate}
                    onChange={(e) => setSalaryForm(prev => ({
                      ...prev,
                      hourlyRate: e?.target?.value
                    }))}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Position
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter position/job title"
                    value={salaryForm?.position}
                    onChange={(e) => setSalaryForm(prev => ({
                      ...prev,
                      position: e?.target?.value
                    }))}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Department
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter department"
                    value={salaryForm?.department}
                    onChange={(e) => setSalaryForm(prev => ({
                      ...prev,
                      department: e?.target?.value
                    }))}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={processing}
                  className="w-full"
                >
                  {processing ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Icon name="Save" size={16} className="mr-2" />
                      Update Salary Information
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <div className="text-center py-12">
                <Icon name="UserCheck" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <h4 className="text-lg font-medium text-foreground mb-2">Select Employee</h4>
                <p className="text-muted-foreground">
                  Choose an employee to update their salary information
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Payroll Records Tab */}
      {activeTab === 'records' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-foreground">Payroll Records</h3>
            
            {/* Filters */}
            <div className="flex items-center space-x-3">
              <select
                className="px-3 py-2 border rounded-md"
                value={filters?.year?.toString()}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  year: parseInt(e?.target?.value)
                }))}
              >
                <option value={new Date()?.getFullYear()?.toString()}>
                  {new Date()?.getFullYear()}
                </option>
                <option value={(new Date()?.getFullYear() - 1)?.toString()}>
                  {new Date()?.getFullYear() - 1}
                </option>
              </select>
              
              <select
                className="px-3 py-2 border rounded-md"
                value={filters?.month?.toString()}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  month: e?.target?.value === '' ? '' : parseInt(e?.target?.value)
                }))}
              >
                <option value="">All Months</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={(i + 1)?.toString()}>
                    {formatEasternDateOnly(new Date(2024, i, 1), { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3]?.map((i) => (
                <div key={i} className="p-4 border rounded-lg animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-muted rounded-full"></div>
                      <div className="space-y-2">
                        <div className="w-40 h-4 bg-muted rounded"></div>
                        <div className="w-32 h-3 bg-muted rounded"></div>
                      </div>
                    </div>
                    <div className="w-24 h-6 bg-muted rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {payrollRecords?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="FileText" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-foreground mb-2">No Payroll Records</h4>
                  <p className="text-muted-foreground">
                    No payroll records found for the selected period
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payrollRecords?.map((record) => (
                    <div key={record?.id} className="p-4 border rounded-lg hover:bg-background/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <Icon name="DollarSign" size={20} color="rgb(34, 197, 94)" />
                          </div>
                          
                          <div className="space-y-1">
                            <p className="font-medium text-foreground">
                              {getEmployeeName(record)}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {getEmployeeId(record)} • {getEmployeePosition(record)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {getEmployeeDepartment(record)} • {formatDate(record?.payPeriodStart)} - {formatDate(record?.payPeriodEnd)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="space-y-1">
                              <p className="font-medium text-foreground">
                                {formatCurrency(record?.netPay)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Gross: {formatCurrency(record?.grossPay)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Processed: {formatDate(record?.processedAt)}
                              </p>
                            </div>
                          </div>
                          
                          {/* Download PDF Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => generatePayrollPDF(record)}
                            className="flex items-center space-x-2"
                          >
                            <Icon name="Download" size={16} />
                            <span>Download PDF</span>
                          </Button>
                        </div>
                      </div>
                      
                      {/* Detailed breakdown */}
                      <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Base:</span>
                          <span className="ml-2">{formatCurrency(record?.baseSalary)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Bonuses:</span>
                          <span className="ml-2 text-green-600">+{formatCurrency(record?.bonuses)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Overtime:</span>
                          <span className="ml-2 text-green-600">+{formatCurrency(record?.overtimePay)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Deductions:</span>
                          <span className="ml-2 text-red-600">-{formatCurrency(record?.deductions)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PayrollManagement;